import type { Incident } from '../lib/types';

interface Props {
  incident: Incident;
}

const IMPACT_BG: Record<string, string> = {
  critical: 'bg-status-major',
  major: 'bg-status-partial',
  minor: 'bg-status-degraded',
  maintenance: 'bg-status-maintenance',
  none: 'bg-[var(--color-bg-subtle)]',
};

const STATUS_LABEL: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function IncidentBanner({ incident }: Readonly<Props>) {
  const bg = IMPACT_BG[incident.impact] || 'bg-[var(--color-bg-subtle)]';
  const latest = incident.updates.at(-1);
  return (
    <div className={`${bg} rounded-md px-5 py-4 text-white`}>
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider opacity-90">
        <span
          className="inline-block h-1.5 w-1.5 animate-status-pulse rounded-full bg-white"
          aria-hidden
        />
        {STATUS_LABEL[incident.status] || incident.status}
        <span aria-hidden>·</span>
        <span>{formatRelative(incident.createdAt)}</span>
      </div>
      <div className="font-display mt-1 text-lg font-medium tracking-tight">{incident.title}</div>
      {latest && <div className="mt-2 text-sm leading-relaxed opacity-95">{latest.message}</div>}
      {incident.components.length > 0 && (
        <div className="mt-3 text-xs opacity-80">
          Affected: <span className="font-medium">{incident.components.join(', ')}</span>
        </div>
      )}
    </div>
  );
}
